@if (showStructureLoading$ | async) {
  <app-step-structure-loading></app-step-structure-loading>
} @else {
<div class="max-w-2xl mx-auto space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
  <!-- Title and Description -->
  <div class="text-center space-y-2 mb-4">
    <h2 class="text-2xl font-bold text-gray-800">Learning Objectives</h2>
    <p class="text-gray-500">Select the goals for this lesson.</p>
  </div>

    @if (objectives$ | async; as objectives) {
      @if (objectives.length === 0) {
        <!-- Empty state - shouldn't happen normally -->
        <div class="text-center py-12 text-gray-500">
          <p>No objectives generated yet.</p>
        </div>
      } @else {
        <div class="space-y-3">
          @for (obj of objectives; track obj.id) {
            <div
              (click)="toggleObjective(obj.id)"
              class="p-4 rounded-xl border-2 transition-all cursor-pointer relative bg-white"
              [class.bg-brand/5]="obj.selected"
              [class.hover:border-gray-300]="!obj.selected"
              [style.borderColor]="obj.selected ? themeColor : '#f3f4f6'"
            >
              <div class="flex gap-3">
                <div
                  class="mt-1 w-5 h-5 shrink-0 rounded border flex items-center justify-center transition-colors"
                  [class.border-transparent]="obj.selected"
                  [class.text-white]="obj.selected"
                  [class.border-gray-300]="!obj.selected"
                  [class.bg-white]="!obj.selected"
                  [style.backgroundColor]="obj.selected ? themeColor : 'white'"
                >
                  @if (obj.selected) {
                    <lucide-icon [img]="icons.CheckCircle2" class="w-3.5 h-3.5"></lucide-icon>
                  }
                </div>
                <div class="flex-1">
                  <div class="flex items-center justify-between gap-2 mb-1">
                    <div class="flex items-center gap-2 flex-wrap">
                      <span
                        class="text-[10px] font-bold uppercase tracking-wider text-gray-500 bg-gray-100 px-2 py-0.5 rounded"
                      >
                        {{ obj.standard }}
                      </span>
                      @if (obj.type === 'manual') {
                        <span class="text-[10px] text-blue-600 bg-blue-50 px-2 py-0.5 rounded font-bold"
                          >You</span
                        >
                      }
                    </div>
                    <div class="flex items-center gap-2 flex-shrink-0">
                      @if (obj.type !== 'manual' && obj.relevance != null) {
                        <span class="relevance-badge text-[10px] font-medium px-2 py-0.5 rounded">
                          {{ getRelevancePercent(obj.relevance) }}
                        </span>
                      }
                      @if (obj.type === 'manual') {
                        <button
                          (click)="openDeleteModal($event, obj)"
                          class="w-6 h-6 flex items-center justify-center rounded text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                          title="Delete objective"
                        >
                          <lucide-icon [img]="icons.Trash2" class="w-4 h-4"></lucide-icon>
                        </button>
                      }
                    </div>
                  </div>
                  <!-- Grades section hidden for now
                  @if (inputData$ | async; as inputData) {
                    <div class="flex items-center gap-1.5 flex-wrap mb-2">
                      <span class="text-[10px] text-gray-400">Aligned to:</span>
                      @for (grade of getDisplayGrades(obj.grades, inputData.grade); track grade) {
                        <span
                          class="grade-badge text-[10px] font-bold px-1.5 py-0.5 rounded whitespace-nowrap"
                          [class.grade-badge--above]="isGradeAboveSelected(grade, inputData.grade)"
                          [class.grade-badge--current]="grade === inputData.grade"
                          [class.grade-badge--below]="isGradeBelowSelected(grade, inputData.grade)"
                        >
                          {{ formatGrade(grade) }}
                        </span>
                      }
                    </div>
                  }
                  -->
                  <p class="text-gray-800 text-sm md:text-base font-medium leading-snug">
                    {{ obj.title }}
                  </p>
                  <p class="text-xs text-gray-500 mt-1 leading-relaxed">
                    {{ obj.text }}
                  </p>
                  @if (obj.type === 'ai' && (obj.alignmentCriteria.length || obj.rationale || obj.standardText)) {
                    <button
                      (click)="toggleDetails($event, obj.id)"
                      class="details-toggle text-xs text-gray-400 mt-2 flex items-center gap-1 hover:text-gray-600 transition-colors cursor-pointer"
                    >
                      <lucide-icon [img]="icons.Info" class="w-3 h-3"></lucide-icon>
                      <span>{{ isDetailsExpanded(obj.id) ? 'Hide' : 'Why this objective?' }}</span>
                      <lucide-icon
                        [img]="isDetailsExpanded(obj.id) ? icons.ChevronUp : icons.ChevronDown"
                        class="w-3 h-3"
                      ></lucide-icon>
                    </button>
                    @if (isDetailsExpanded(obj.id)) {
                      <div class="mt-2 pl-4 border-l-2 border-gray-200 animate-fade-in space-y-2">
                        @if (obj.alignmentCriteria.length) {
                          <div>
                            <span class="text-[10px] font-bold uppercase text-gray-400">Alignment Criteria</span>
                            <ul class="mt-1 space-y-1">
                              @for (criterion of obj.alignmentCriteria; track criterion) {
                                <li class="text-xs text-gray-600 flex items-start gap-1.5">
                                  <lucide-icon [img]="icons.Check" class="w-3 h-3 text-green-500 mt-0.5 shrink-0"></lucide-icon>
                                  <span>{{ criterion }}</span>
                                </li>
                              }
                            </ul>
                          </div>
                        }
                        @if (obj.rationale) {
                          <div>
                            <span class="text-[10px] font-bold uppercase text-gray-400">Rationale</span>
                            <p class="text-xs text-gray-500 mt-0.5">{{ obj.rationale }}</p>
                          </div>
                        }
                        @if (obj.standardText) {
                          <div>
                            <span class="text-[10px] font-bold uppercase text-gray-400">Original Standard</span>
                            <p class="text-xs text-gray-500 mt-0.5 italic">{{ obj.standardText }}</p>
                          </div>
                        }
                      </div>
                    }
                  }
                </div>
              </div>
            </div>
          }

          <!-- Add Custom Objective -->
          <div
            class="p-4 rounded-xl border-2 border-dashed border-gray-200 bg-gray-50/50 flex flex-col gap-3"
          >
            <label class="text-xs font-bold text-gray-400 uppercase">Add your own objective</label>
            <div class="flex gap-2 items-start">
              <textarea
                #customTextarea
                appAutoResizeTextarea
                [(ngModel)]="customText"
                placeholder="Type goal..."
                rows="1"
                class="flex-1 p-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand/20 resize-none min-h-10 overflow-hidden"
              ></textarea>
              <button
                (click)="addCustomObjective(customTextarea)"
                [disabled]="!customText"
                class="w-10 h-10 shrink-0 flex items-center justify-center rounded-lg text-white font-bold disabled:opacity-50 cursor-pointer"
                [style.backgroundColor]="themeColor"
              >
                <lucide-icon [img]="icons.Plus" class="w-5 h-5"></lucide-icon>
              </button>
            </div>
          </div>
        </div>

        <!-- Error Message -->
        @if (error$ | async; as error) {
          <div class="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
            {{ error }}
          </div>
        }

        <!-- Navigation Buttons -->
        <div class="flex gap-3 pt-4 pb-8">
          <button
            (click)="onBack()"
            class="flex-1 py-3 text-gray-500 font-medium bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors cursor-pointer"
          >
            Back
          </button>
          <button
            (click)="onContinue()"
            [disabled]="getSelectedCount(objectives) === 0"
            class="flex-[2] py-3 text-white font-bold rounded-xl shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            [style.backgroundColor]="themeColor"
          >
            Confirm ({{ getSelectedCount(objectives) }})
          </button>
        </div>
      }
    }
</div>
}

<!-- Delete Confirmation Modal -->
<app-confirm-modal
  [isOpen]="isDeleteModalOpen"
  title="Delete Objective"
  message="Are you sure you want to delete this objective? This action cannot be undone."
  confirmText="Delete"
  cancelText="Cancel"
  confirmColor="#ef4444"
  (confirmed)="confirmDelete()"
  (cancelled)="closeDeleteModal()"
></app-confirm-modal>
